import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AuthModal from '../../../components/AuthModal';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, minecraftUsername, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowProfileMenu(false);
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error);
    }
  };

  // Minecraft skin head URL'i oluştur
  const getMinecraftHeadUrl = (username: string) => {
    return `https://mc-heads.net/avatar/${username}/64`;
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-gamepad-line text-2xl text-white"></i>
              </div>
              <span
                className={`text-2xl font-bold ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                Windo5DJ
              </span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#home"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Ana Sayfa
              </a>
              <a
                href="#features"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Özellikler
              </a>
              <a
                href="#gallery"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Galeri
              </a>
              <a
                href="/market"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Market
              </a>
              <a
                href="#contact"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                İletişim
              </a>

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all cursor-pointer"
                  >
                    <img
                      src={getMinecraftHeadUrl(minecraftUsername || 'Steve')}
                      alt={minecraftUsername || 'Player'}
                      className="w-8 h-8 rounded-full"
                      onError={(e) => {
                        e.currentTarget.src = 'https://mc-heads.net/avatar/Steve/64';
                      }}
                    />
                    <span className={`font-medium ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                      {minecraftUsername || 'Oyuncu'}
                    </span>
                    <i className={`ri-arrow-down-s-line ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
                  </button>

                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl py-2 border border-gray-100">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <img
                            src={getMinecraftHeadUrl(minecraftUsername || 'Steve')}
                            alt={minecraftUsername || 'Player'}
                            className="w-12 h-12 rounded-lg"
                            onError={(e) => {
                              e.currentTarget.src = 'https://mc-heads.net/avatar/Steve/64';
                            }}
                          />
                          <div>
                            <p className="font-semibold text-gray-900">{minecraftUsername}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2 cursor-pointer"
                      >
                        <i className="ri-logout-box-line"></i>
                        <span>Çıkış Yap</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setAuthMode('login');
                      setShowAuthModal(true);
                    }}
                    className={`px-6 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    Giriş Yap
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('register');
                      setShowAuthModal(true);
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-teal-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Kayıt Ol
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a
                href="#home"
                className={`block py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Ana Sayfa
              </a>
              <a
                href="#features"
                className={`block py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Özellikler
              </a>
              <a
                href="#gallery"
                className={`block py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Galeri
              </a>
              <a
                href="/market"
                className={`block py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                Market
              </a>
              <a
                href="#contact"
                className={`block py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                İletişim
              </a>

              {user ? (
                <div className="pt-3 border-t border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <img
                      src={getMinecraftHeadUrl(minecraftUsername || 'Steve')}
                      alt={minecraftUsername || 'Player'}
                      className="w-10 h-10 rounded-lg"
                      onError={(e) => {
                        e.currentTarget.src = 'https://mc-heads.net/avatar/Steve/64';
                      }}
                    />
                    <div>
                      <p className={`font-semibold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                        {minecraftUsername}
                      </p>
                      <p className={`text-sm ${isScrolled ? 'text-gray-600' : 'text-white/70'}`}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Çıkış Yap
                  </button>
                </div>
              ) : (
                <div className="pt-3 border-t border-white/20 space-y-2">
                  <button
                    onClick={() => {
                      setAuthMode('login');
                      setShowAuthModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full px-6 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                      isScrolled
                        ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                        : 'text-white bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    Giriş Yap
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('register');
                      setShowAuthModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-teal-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Kayıt Ol
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </>
  );
}
